import React, { useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import MySkeleton from "components/ui/feedback/Skeleton";
import { useStatsFetch } from "./useStatsApi";
import { StatCard, SectionCard, RefreshButton, ErrorMessage, SimpleTable, formatNumber } from "./StatComponents";
import AppVersionStats from "./AppVersionStats";
import SchoolLoginRates from "./SchoolLoginRates";

const DAY_OPTIONS = [7, 14, 30, 60];

const DistributionBlock = ({ title, distribution, emptyText }) => {
  if (!distribution) return null;

  const columns = [
    { key: "key", label: "Değer" },
    { key: "count", label: "Adet", align: "right", render: (row) => formatNumber(row.count) },
    {
      key: "rate",
      label: "Oran",
      align: "right",
      render: (row) => (distribution.total > 0 ? ((row.count / distribution.total) * 100).toFixed(1) + "%" : "-"),
    },
  ];

  return (
    <div style={{ marginBottom: "20px" }}>
      <h4 style={{ margin: "0 0 10px 0", fontSize: "14px" }}>
        {title} ({formatNumber(distribution.total)} kayıt)
      </h4>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "20px" }}>
        <div style={{ flex: "1 1 220px", minWidth: "220px" }}>
          <h5 style={{ margin: "0 0 6px 0", fontSize: "13px", color: "#666" }}>Platform</h5>
          <SimpleTable columns={columns} rows={distribution.platforms} maxHeight="300px" emptyText={emptyText} />
        </div>
        <div style={{ flex: "1 1 220px", minWidth: "220px" }}>
          <h5 style={{ margin: "0 0 6px 0", fontSize: "13px", color: "#666" }}>İşletim Sistemi</h5>
          <SimpleTable columns={columns} rows={distribution.osVersions} maxHeight="300px" emptyText={emptyText} />
        </div>
        <div style={{ flex: "1 1 220px", minWidth: "220px" }}>
          <h5 style={{ margin: "0 0 6px 0", fontSize: "13px", color: "#666" }}>Uygulama Sürümü</h5>
          <SimpleTable columns={columns} rows={distribution.appVersions} maxHeight="300px" emptyText={emptyText} />
        </div>
      </div>
    </div>
  );
};

const DayDetails = ({ title, data }) => {
  if (!data) return null;
  return (
    <SectionCard title={title}>
      <DistributionBlock
        title="Görülen Cihazlar (Mobile kaydı o gün güncellenenler)"
        distribution={data.devices}
        emptyText="Kayıt yok."
      />
      <DistributionBlock
        title="Giriş Kayıtları (UserPhone)"
        distribution={data.logins}
        emptyText="Kayıt yok."
      />
    </SectionCard>
  );
};

const LoginStatsSection = () => {
  const [days, setDays] = useState(14);
  const { data, isLoading, error, load } = useStatsFetch(`/admin/statistics/login-stats?days=${days}`);

  const chartData = (data?.daily || []).map((d) => ({
    ...d,
    shortDate: d.date.slice(5), // MM/DD
  }));

  const sumField = (field) => (data?.daily || []).reduce((sum, d) => sum + (d[field] || 0), 0);

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "10px",
          marginBottom: "15px",
        }}
      >
        <div style={{ display: "flex", gap: "6px" }}>
          {DAY_OPTIONS.map((option) => (
            <button
              key={option}
              onClick={() => setDays(option)}
              style={{
                padding: "6px 12px",
                borderRadius: "4px",
                border: days === option ? "1px solid #1976d2" : "1px solid #ccc",
                background: days === option ? "#1976d2" : "#fff",
                color: days === option ? "#fff" : "#333",
                cursor: "pointer",
                fontSize: "13px",
              }}
            >
              {option} gün
            </button>
          ))}
        </div>
        <RefreshButton onClick={() => load()} isLoading={isLoading} />
      </div>

      <ErrorMessage message={error} />

      {isLoading && !data ? (
        <MySkeleton lineCount={8} />
      ) : data ? (
        <>
          <div
            style={{
              padding: "10px 14px",
              background: "#fff3e0",
              border: "1px solid #ffb74d",
              borderRadius: "6px",
              fontSize: "13px",
              marginBottom: "15px",
              color: "#7a4f01",
            }}
          >
            <strong>Önemli:</strong> Bu veriler "son görülme" alanlarından üretilir; her cihaz yalnızca{" "}
            <strong>en son</strong> görüldüğü günde sayılır. Bu yüzden geçmiş günler gerçek aktiviteden{" "}
            <strong>daha düşük</strong> görünür. En güvenilir değerler bugüne aittir. Kullanıcılar token ile
            oturumda kaldığı için "OTP Girişi" gerçek günlük kullanıcı sayısı değil, o gün şifreyle yeniden giriş
            yapanlardır.
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "15px", marginBottom: "20px" }}>
            <StatCard
              label="Bugün Görülen Cihaz"
              value={data.today?.devices?.total}
              sub="Mobile kaydı bugün güncellenenler"
              color="#2e7d32"
            />
            <StatCard
              label="Dün Görülen Cihaz"
              value={data.yesterday?.devices?.total}
              sub="Mobile kaydı dün güncellenenler"
              color="#66bb6a"
            />
            <StatCard
              label="Bugün Giriş Kaydı"
              value={data.today?.logins?.total}
              sub="UserPhone bugün güncellenenler"
              color="#1976d2"
            />
            <StatCard
              label="Dün Giriş Kaydı"
              value={data.yesterday?.logins?.total}
              sub="UserPhone dün güncellenenler"
              color="#64b5f6"
            />
            <StatCard
              label={`Son ${days} Günde Görülen Cihaz`}
              value={sumField("activeDevices")}
              sub="Son görülme dağılımı toplamı"
              color="#7b1fa2"
            />
          </div>

          <SectionCard title={`Son Görülme / Giriş Dağılımı (Son ${days} Gün)`}>
            <div style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer>
                <LineChart data={chartData} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="shortDate" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                  <Tooltip
                    formatter={(value, name) => [formatNumber(value), name]}
                    labelFormatter={(label, payload) => payload?.[0]?.payload?.date || label}
                  />
                  <Legend wrapperStyle={{ fontSize: "13px" }} />
                  <Line
                    type="monotone"
                    dataKey="activeDevices"
                    name="Görülen Cihaz (Mobile)"
                    stroke="#1976d2"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="otpLogins"
                    name="OTP Girişi (Mobile)"
                    stroke="#2e7d32"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="userPhoneLogins"
                    name="Giriş Kaydı (UserPhone)"
                    stroke="#9e9e9e"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={{ r: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </SectionCard>

          <DayDetails title="Bugünkü Dağılım" data={data.today} />
          <DayDetails title="Dünkü Dağılım" data={data.yesterday} />
        </>
      ) : null}

      <AppVersionStats />
      <SchoolLoginRates />
    </div>
  );
};

export default LoginStatsSection;
